import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import { UNSAFE_NavigationContext as NavigationContext, useParams } from 'react-router-dom';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch } from 'react-redux';

// Utils
import {
  useAuthSequencingFilters,
  useCardChangeTracking,
  useIntersect,
  useUserRolePermissions,
} from 'hooks';

// Components
import { MDialog } from 'common';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import FilterableContent from 'common/templates/FilterableContent';
import { DrawerContext } from 'common/drawer/DrawerContext';
import { openConfirmationDialog } from 'store/Confirmationdialogslice';
import EditFutureSequenceDrawer from 'feature/loading-management/authorization-sequencing/edit-panel/EditFutureSequenceDrawer';

import GroupedCardContent from 'common/template-parts/GroupedCardContent';
import CustomizableCard, { CARD_STATUS } from 'common/template-parts/CustomizableCard';
import { createSnack } from 'snackbar/snackbarSlice';
import NoResultsBanner from 'common/template-parts/NoResultsBanner';
import { AuthContext } from 'authentication/AuthContext';
import AuthSequencingCardFooter from 'feature/loading-management/authorization-sequencing/templates/AuthSequencingCardFooter';
import AuthSequencingFilterContent from 'feature/loading-management/authorization-sequencing/templates/AuthSequencingFilterContent';
import AuthSequencingMenu from 'feature/loading-management/authorization-sequencing/templates/AuthSequencingMenu';
import AuthorizationSequencingCardContent from 'feature/loading-management/authorization-sequencing/templates/AuthorizationSequencingCardContent';
import LoadableChip from 'feature/loading-management/authorization-sequencing/templates/LoadableChip';
import CustomizableCardSkeleton from 'common/template-parts/CustomizableCardSkeleton';
import TerminalAlertsIndicator from 'common/template-parts/TerminalAlertsIndicator';
import {
  useGetAuthSeqListQuery,
  useUpdateAuthorizationSequencingMutation,
} from './authorizationSequencingEndpoints';
import InitialStep from './sequencing-wizard/InitialStep';
import SequencingWizard from './sequencing-wizard/SequencingWizardContext';
import { isAuthSequencingUpdatesEnabled } from '../utils/const';

// Steps
const steps = {
  last: [
    {
      id: 1,
      stepLabel: 'Inventory Owner',
    },
    {
      id: 2,
      stepLabel: 'Products',
    },
    {
      id: 3,
      stepLabel: 'Locations',
    },
    {
      id: 4,
      stepLabel: 'Future Date',
    },
    {
      id: 5,
      stepLabel: 'Summary',
    },
    {
      id: 6,
      stepLabel: 'Progress',
    },
  ],
  primary: [
    {
      id: 1,
      stepLabel: 'Inventory Owner',
    },
    {
      id: 2,
      stepLabel: 'Products',
    },
    {
      id: 3,
      stepLabel: 'Locations',
    },
    {
      id: 4,
      stepLabel: 'Future Date',
    },
    {
      id: 5,
      stepLabel: 'Summary',
    },
    {
      id: 6,
      stepLabel: 'Progress',
    },
  ],
  remove: [
    {
      id: 1,
      stepLabel: 'Inventory Owners',
    },
    {
      id: 2,
      stepLabel: 'Products',
    },
    {
      id: 3,
      stepLabel: 'Locations',
    },
    {
      id: 4,
      stepLabel: 'Future Date',
    },
    {
      id: 5,
      stepLabel: 'Summary',
    },
    {
      id: 6,
      stepLabel: 'Progress',
    },
  ],
  sequence: [
    {
      id: 1,
      stepLabel: 'Inventory Owners',
    },
    {
      id: 2,
      stepLabel: 'Products',
    },
    {
      id: 3,
      stepLabel: 'Locations',
    },
    {
      id: 4,
      stepLabel: 'Sequence Authorization',
    },
    {
      id: 5,
      stepLabel: 'Future Date',
    },
    {
      id: 6,
      stepLabel: 'Summary',
    },
    {
      id: 7,
      stepLabel: 'Progress',
    },
  ],
};

export const AUTH_SEQ_PERSISTED_FILTER_KEYS = ['nameShort', 'relatedIds', 'searchTerm', 'state'];

const isPersistedAuthSeqFilterApplied = value => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (R.isNil(value)) {
    return false;
  }

  const normalized = value.trim().toLowerCase();
  return normalized !== '' && normalized !== 'any' && normalized !== 'all';
};

export const hasPersistedAuthSeqFilters = persistedFilters =>
  AUTH_SEQ_PERSISTED_FILTER_KEYS.some(key =>
    isPersistedAuthSeqFilterApplied(persistedFilters?.[key])
  );

const UNSAVED_AUTH_SEQ_MESSAGE =
  'Are you sure you want to leave this page? You have unsaved Authorization Sequencing changes.';

const CARD_VISIBILITY_ROOT_MARGIN = '150px';
const INITIAL_VISIBLE_CARD_COUNT = 6;

// Custom hook for blocking navigation when there are unsaved changes
const useNavigationBlocker = (blocker, when = true) => {
  const { navigator } = useContext(NavigationContext);

  useEffect(() => {
    if (!when) return undefined;
    const suppressBrowserUnloadPrompt = event => {
      event.stopImmediatePropagation();
    };
    window.addEventListener('beforeunload', suppressBrowserUnloadPrompt);

    const unblock = navigator.block(transaction => {
      const autoUnblockingTransaction = {
        ...transaction,
        retry() {
          unblock();
          transaction.retry();
        },
      };

      blocker(autoUnblockingTransaction);
    });

    return () => {
      window.removeEventListener('beforeunload', suppressBrowserUnloadPrompt);
      unblock();
    };
  }, [blocker, navigator, when]);
};

const AuthSequencingLazyCard = React.memo(
  ({
    appliedFilters,
    authData,
    cardRefs,
    getCardHasChanges,
    getCardRef,
    getCardStatus,
    handleCardChange,
    inCardEditingFlag,
    index,
    isProductLoadingManage,
    onSave,
    setCardStatus,
    setDrawerComponentProps,
  }) => {
    const { entry, setNode } = useIntersect({ rootMargin: CARD_VISIBILITY_ROOT_MARGIN });
    const cardContainerRef = useRef();
    const [hasBeenVisible, setHasBeenVisible] = useState(index < INITIAL_VISIBLE_CARD_COUNT);

    useEffect(() => {
      setNode(cardContainerRef.current);
    }, [setNode]);

    const { intersectionRatio, isIntersecting } = entry;

    useEffect(() => {
      if (hasBeenVisible) return;

      if (isIntersecting || intersectionRatio > 0) {
        setHasBeenVisible(true);
      }
    }, [hasBeenVisible, intersectionRatio, isIntersecting]);

    if (!hasBeenVisible) {
      return (
        <Box ref={cardContainerRef}>
          <CustomizableCardSkeleton />
        </Box>
      );
    }

    const cardStatus = getCardStatus(authData.locationId, authData.productId);
    const isCardProcessing = cardStatus === CARD_STATUS.PROGRESS;

    return (
      <Box ref={cardContainerRef}>
        <CustomizableCard
          cardContent={
            <AuthorizationSequencingCardContent
              authSequenceData={authData}
              canEdit={isProductLoadingManage && inCardEditingFlag && !isCardProcessing}
              onAuthSequenceChange={newState => {
                const cardRef = getCardRef(authData.locationId, authData.productId);
                const cardHasChanges = cardRef?.current?.compareStateToOriginal?.(newState) ?? true;
                handleCardChange(authData.locationId, authData.productId, cardHasChanges);
              }}
              onFutureAuthSequenceClick={() => {
                setDrawerComponentProps({
                  Component: drawerProps => (
                    <EditFutureSequenceDrawer
                      {...drawerProps}
                      // eslint-disable-next-line react/prop-types
                      affiliate={appliedFilters.affiliate}
                      isReadOnly={!isProductLoadingManage}
                      locationId={authData.locationId}
                      productId={authData.productId}
                    />
                  ),
                  forceCloseSelector: () => {},
                });
              }}
              onStatusChange={status =>
                setCardStatus(authData.locationId, authData.productId, status)
              }
              ref={getCardRef(authData.locationId, authData.productId)}
            />
          }
          cardFooter={
            <AuthSequencingCardFooter
              cardRefs={cardRefs}
              getCardHasChanges={getCardHasChanges}
              handleCardChange={handleCardChange}
              isProcessing={isCardProcessing}
              locationId={authData.locationId}
              onCardCancel={(locationId, productId) => {
                // Reset card state and clear changes
                const cardRef = getCardRef(locationId, productId);
                cardRef?.current?.reset();
                handleCardChange(locationId, productId, false);
              }}
              onCardSave={(locationId, productId) => {
                onSave(locationId, productId);
              }}
              productId={authData.productId}
            />
          }
          cardHeader={authData.productNameLong || authData.product?.nameLong || 'Unknown Product'}
          cardHeaderRightSideContent={<LoadableChip loadingAllowed={authData.loadingAllowed} />}
          status={cardStatus}
        />
      </Box>
    );
  }
);

AuthSequencingLazyCard.propTypes = {
  appliedFilters: PropTypes.shape({}).isRequired,
  authData: PropTypes.shape({
    loadingAllowed: PropTypes.bool,
    locationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    product: PropTypes.shape({
      nameLong: PropTypes.string,
    }),
    productId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    productNameLong: PropTypes.string,
  }).isRequired,
  cardRefs: PropTypes.shape({}).isRequired,
  getCardHasChanges: PropTypes.func.isRequired,
  getCardRef: PropTypes.func.isRequired,
  getCardStatus: PropTypes.func.isRequired,
  handleCardChange: PropTypes.func.isRequired,
  inCardEditingFlag: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  isProductLoadingManage: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  setCardStatus: PropTypes.func.isRequired,
  setDrawerComponentProps: PropTypes.func.isRequired,
};

const AuthorizationSequencing = () => {
  const dispatch = useDispatch();
  const { setDrawerComponentProps } = useContext(DrawerContext);
  const {
    affiliateOptions,
    appliedFilters,
    canFetchData,
    draftFilters,
    handleApply,
    handleReset,
    hasChanges,
    hasMinimumFilters,
    isOnDefaultState,
    setAffiliateFilter,
    setLocationFilter,
    setProductFilter,
    setSearch,
    setStatusFilter,
  } = useAuthSequencingFilters();
  const { currentAffiliate } = useContext(AuthContext);
  const [updateAuthorizationSequencing] = useUpdateAuthorizationSequencingMutation();
  // Build query params from applied filters for the view-authorization-sequencing endpoint
  const queryParams = useMemo(() => {
    if (!canFetchData) return null;

    const params = {
      'Supplier.CompanyCustomerIdName': appliedFilters.affiliate?.customerIdName,
      'Supplier.DataCompanyId': appliedFilters.affiliate?.dataCompanyId,
    };

    if (appliedFilters.product.length) {
      params.ProductIds = appliedFilters.product.map(p => p.value);
    }
    if (appliedFilters.location.length) {
      params.LocationIds = appliedFilters.location.map(l => l.value);
    }
    if (appliedFilters.status && appliedFilters.status !== 'All') {
      params.Status =
        appliedFilters.status === 'Not Loadable' ? 'NotLoadable' : appliedFilters.status;
    } else {
      params.Status = 'All';
    }

    // Add search text if present
    if (appliedFilters.search && appliedFilters.search.trim()) {
      params.quickFilter = appliedFilters.search.trim();
    }

    return params;
  }, [appliedFilters, canFetchData]);

  const { data: authSeqListData } = useGetAuthSeqListQuery(queryParams, {
    skip: !queryParams,
  });
  const {
    cardRefs,
    getCardRef,
    handleCardChange,
    getCardHasChanges,
    setCardStatus,
    getCardStatus,
  } = useCardChangeTracking();

  // Group data by location for GroupedCardContent
  const groupedData = useMemo(() => {
    if (!authSeqListData || !Array.isArray(authSeqListData)) {
      return {};
    }

    const grouped = authSeqListData.reduce((acc, authData) => {
      const locationKey = authData.locationNameLong || authData.nameLong;

      // If authData has a products array (grouped API response format)
      if (authData.products && Array.isArray(authData.products)) {
        // Enrich each product with locationId and locationNameLong from parent
        const enrichedProducts = authData.products.map(product => ({
          ...product,
          locationId: authData.locationId,
          locationIdName: authData.locationIdName,
          locationNameLong: authData.locationNameLong,
        }));
        acc[locationKey] = enrichedProducts;
      }
      // If authData is a flat product object (flat API response format)
      else {
        if (!acc[locationKey]) {
          acc[locationKey] = [];
        }
        acc[locationKey].push(authData);
      }

      return acc;
    }, {});

    return grouped;
  }, [authSeqListData]);

  // viewBy is based on the URL, can be accessed like this anywhere in this section
  const { viewBy } = useParams();
  const { isSubscriber } = useUserRolePermissions();
  const isAuthSequencingUpdatesFlagOn = isAuthSequencingUpdatesEnabled();
  const { isProductLoadingManage } = useUserRolePermissions('isProductLoadingManage');
  const inCardEditingFlag = EvaluateFlag(
    process.env.REACT_APP_LD_AUTHORIZATION_SEQUENCING_IN_CARD_EDITING
  );

  // Persist viewBy when it changes
  useEffect(() => {
    if (!isAuthSequencingUpdatesFlagOn) return;
    try {
      localStorage.setItem('authorization-sequencing.view', viewBy);
    } catch (e) {
      // Silently fail
    }
  }, [isAuthSequencingUpdatesFlagOn, viewBy]);

  const isSequencingWizardEnabled = EvaluateFlag(
    process.env.REACT_APP_LD_AUTHORIZATION_SEQUENCING_WIZARD
  );

  const isSequencingWizardForAllCustomers = EvaluateFlag(
    process.env.REACT_APP_LD_AUTHORIZATION_SEQUENCING_WIZARD_FOR_ALL_CUSTOMERS
  );

  // Derived access: wizard is accessible if the AllCustomers flag is ON, or user is a Swift+ subscriber.
  const hasWizardAccess = isSequencingWizardForAllCustomers || isSubscriber;

  // State values for the Sequencing Wizard
  // sets open
  const [wizardOpen, setWizardOpen] = useState(false);
  // sets initial component inside the drawer
  const [children, setChildren] = useState(<InitialStep key="initialStep" />);
  // opens the dialog
  const [dialogOpen, setDialogOpen] = useState(false);

  const tooltip =
    'Sequencing Wizard is available for Affiliates with a Swift+ enrollment. Please select an Affiliate that is enrolled in Swift+';

  // Track unsaved changes for navigation blocking
  const allowNavigationRef = useRef(false);
  const allowedUnsavedFilterActionCountRef = useRef(0);
  const [isUnsavedDialogOpen, setIsUnsavedDialogOpen] = useState(false);

  // Get all cards with unsaved changes
  const allChangedCards = useMemo(() => {
    if (!groupedData) return [];
    const changedCards = [];
    Object.values(groupedData).forEach(products => {
      if (Array.isArray(products)) {
        products.forEach(product => {
          if (getCardHasChanges(product.locationId, product.productId)) {
            changedCards.push(product);
          }
        });
      }
    });
    return changedCards;
  }, [groupedData, getCardHasChanges]);

  const hasUnsavedChanges = allChangedCards.length > 0;

  const discardUnsavedCardChanges = useCallback(() => {
    Object.keys(cardRefs.current || {}).forEach(cardKey => {
      const [locationId, productId] = cardKey.split('-').map(Number);
      const cardRef = cardRefs.current[cardKey];

      // If the card is currently mounted, reset its local sequence state.
      cardRef?.current?.reset?.();
      handleCardChange(locationId, productId, false);
    });
  }, [cardRefs, handleCardChange]);

  const openUnsavedChangesDialog = useCallback(
    ({ confirmAction, message }) => {
      if (isUnsavedDialogOpen) return;

      setIsUnsavedDialogOpen(true);

      dispatch(
        openConfirmationDialog({
          cancelAction: () => {
            setIsUnsavedDialogOpen(false);
          },
          confirmAction: () => {
            setIsUnsavedDialogOpen(false);
            confirmAction?.();
          },
          header: 'Unsaved Changes',
          leftButtonActionType: 'confirm',
          leftButtonText: 'Continue',
          message,
          params: {},
          rightButtonActionType: 'cancel',
          rightButtonText: 'Keep Editing',
        })
      );
    },
    [dispatch, isUnsavedDialogOpen]
  );

  const runWithUnsavedChangesGuard = useCallback(
    ({ action, allowedFollowUpActions = 0, message }) => {
      if (!hasUnsavedChanges) {
        action?.();
        return;
      }

      if (allowedUnsavedFilterActionCountRef.current > 0) {
        allowedUnsavedFilterActionCountRef.current -= 1;
        action?.();
        return;
      }

      openUnsavedChangesDialog({
        confirmAction: () => {
          discardUnsavedCardChanges();
          allowedUnsavedFilterActionCountRef.current = allowedFollowUpActions;
          action?.();
        },
        message,
      });
    },
    [discardUnsavedCardChanges, hasUnsavedChanges, openUnsavedChangesDialog]
  );

  const handleApplyWithUnsavedChanges = useCallback(() => {
    runWithUnsavedChangesGuard({
      action: handleApply,
      message: UNSAVED_AUTH_SEQ_MESSAGE,
    });
  }, [handleApply, runWithUnsavedChangesGuard]);

  const handleResetWithUnsavedChanges = useCallback(() => {
    runWithUnsavedChangesGuard({
      action: handleReset,
      message: UNSAVED_AUTH_SEQ_MESSAGE,
    });
  }, [handleReset, runWithUnsavedChangesGuard]);

  const handleSearchWithUnsavedChanges = useCallback(
    value => {
      runWithUnsavedChangesGuard({
        action: () => setSearch(value),
        allowedFollowUpActions: 1,
        message: UNSAVED_AUTH_SEQ_MESSAGE,
      });
    },
    [runWithUnsavedChangesGuard, setSearch]
  );

  const handleAffiliateWithUnsavedChanges = useCallback(
    value => {
      runWithUnsavedChangesGuard({
        action: () => setAffiliateFilter(value),
        message: UNSAVED_AUTH_SEQ_MESSAGE,
      });
    },
    [runWithUnsavedChangesGuard, setAffiliateFilter]
  );

  // Handle blocked navigation when there are unsaved changes
  const handleBlockedNavigation = useCallback(
    transaction => {
      if (allowNavigationRef.current) {
        allowNavigationRef.current = false;
        transaction.retry();
        return;
      }

      openUnsavedChangesDialog({
        confirmAction: () => {
          const navigation = transaction;
          allowNavigationRef.current = true;
          navigation?.retry?.();
        },
        message: UNSAVED_AUTH_SEQ_MESSAGE,
      });
    },
    [openUnsavedChangesDialog]
  );

  // Block navigation when there are unsaved changes
  useNavigationBlocker(handleBlockedNavigation, hasUnsavedChanges);

  // Set global flag for unsaved changes (used by other parts of the app)
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    window.__authSeqHasUnsavedChanges = hasUnsavedChanges;

    return () => {
      window.__authSeqHasUnsavedChanges = false;
    };
  }, [hasUnsavedChanges]);

  const mapSavedAuthorization = auth => ({
    futureSequenceDate: auth.futureSequenceDate,
    inventoryOwner:
      typeof auth.inventoryOwner === 'string'
        ? auth.inventoryOwner
        : auth.inventoryOwner?.nameShort || auth.inventoryOwner?.companyCustomerIdName || '',
    isActive: auth.active,
    productAuthorizationId: auth.productAuthorizationId,
    sequenceNumber: auth.sequenceNumber,
    supplierDirectedBlend: auth.supplierDirectedBlend,
  });

  const onSave = useCallback(
    async (locationId, productId) => {
      const cardRef = getCardRef(locationId, productId);
      const currentAuthData = cardRef?.current?.getCurrentState();

      if (!currentAuthData) {
        return;
      }

      cardRef?.current?.showSavingState();
      const body = {
        authorizationSequence: currentAuthData.sequenced.map(auth => auth.productAuthorizationId),
        futureSequence: {
          futureAuthorizationSequence: [],
          futureSequenceDate: null,
        },
        locationId,
        productId,
        supplier: {
          companyCustomerIdName:
            appliedFilters.affiliate?.customerIdName || currentAffiliate?.customerIdName,
          dataCompanyId: appliedFilters.affiliate?.dataCompanyId || currentAffiliate?.dataCompanyId,
        },
      };

      try {
        const response = await updateAuthorizationSequencing(body).unwrap();

        cardRef?.current?.applySavedState({
          notSequenced: (response.unsequencedAuthorizations || []).map(mapSavedAuthorization),
          sequenced: (response.sequencedAuthorizations || []).map(mapSavedAuthorization),
        });

        cardRef?.current?.showSuccessState();
        handleCardChange(locationId, productId, false);
      } catch (error) {
        const message = error?.data?.detail || error?.data?.title || error?.error || 'an error';

        cardRef?.current?.showErrorState();
        handleCardChange(locationId, productId, true);
        dispatch(
          createSnack({
            message: `Due to ${message}, your sequence has not been saved.`,
            options: { persist: true, variant: 'error' },
            title: 'Action Not Completed',
          })
        );
      }
    },
    [
      appliedFilters.affiliate?.customerIdName,
      appliedFilters.affiliate?.dataCompanyId,
      currentAffiliate?.customerIdName,
      currentAffiliate?.dataCompanyId,
      dispatch,
      getCardRef,
      handleCardChange,
      updateAuthorizationSequencing,
    ]
  );

  const renderAuthSequencingCard = useCallback(
    (authData, index) => (
      <AuthSequencingLazyCard
        appliedFilters={appliedFilters}
        authData={authData}
        cardRefs={cardRefs}
        getCardHasChanges={getCardHasChanges}
        getCardRef={getCardRef}
        getCardStatus={getCardStatus}
        handleCardChange={handleCardChange}
        inCardEditingFlag={inCardEditingFlag}
        index={index}
        isProductLoadingManage={isProductLoadingManage}
        onSave={onSave}
        setCardStatus={setCardStatus}
        setDrawerComponentProps={setDrawerComponentProps}
      />
    ),
    [
      appliedFilters,
      cardRefs,
      getCardHasChanges,
      getCardRef,
      getCardStatus,
      handleCardChange,
      inCardEditingFlag,
      isProductLoadingManage,
      onSave,
      setCardStatus,
      setDrawerComponentProps,
    ]
  );

  return (
    <>
      <FilterableContent
        canToggleFilterDrawer
        filterContent={
          <AuthSequencingFilterContent
            affiliateFilter={draftFilters.affiliate}
            affiliateOptions={affiliateOptions}
            locationFilter={draftFilters.location}
            productFilter={draftFilters.product}
            setAffiliateFilter={handleAffiliateWithUnsavedChanges}
            setLocationFilter={setLocationFilter}
            setProductFilter={setProductFilter}
            setStatusFilter={setStatusFilter}
            statusFilter={draftFilters.status}
          />
        }
        handleReset={handleResetWithUnsavedChanges}
        hasChanges={hasChanges && hasMinimumFilters}
        headerTitle={`Authorization Sequencing ${hasUnsavedChanges ? '*' : ''}`}
        isOnDefaultState={isOnDefaultState}
        onApply={handleApplyWithUnsavedChanges}
        onSearchChange={handleSearchWithUnsavedChanges}
        primaryFilterActionButtonLabel="Apply Filters"
        searchPlaceholder="Search authorizations..."
        searchValue={draftFilters.search}
        secondaryFilterActionButtonLabel="Reset Filters"
        showQuickSearch
        titleMenu={
          <AuthSequencingMenu
            buttons={
              isSequencingWizardEnabled &&
              isProductLoadingManage && (
                <>
                  <Tooltip
                    disableHoverListener={hasWizardAccess}
                    enterTouchDelay={10}
                    placement="top"
                    title={tooltip}
                  >
                    <div>
                      <Button
                        // disabled={authorizationSequencingPending || !hasWizardAccess}
                        onClick={() => setWizardOpen(true)}
                      >
                        {/* {authorizationSequencingPending && <ButtonProgress size={14} />} */}
                        Launch Sequencing Wizard
                      </Button>
                    </div>
                  </Tooltip>
                </>
              )
            }
            hasWizardAccess={hasWizardAccess}
            isSequencingWizardEnabled={isSequencingWizardEnabled}
            setWizardOpen={setWizardOpen}
            tooltip={tooltip}
            viewBy={viewBy}
          />
        }
      >
        {appliedFilters.product.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <NoResultsBanner
              icon={<SearchIcon fontSize="inherit" />}
              primaryText="Select a product to see Authorization Sequences"
              secondaryText="Start by applying at least 1 product filter to see results."
            />
          </Box>
        ) : (
          <GroupedCardContent
            getGroupLink={product =>
              `../terminal-information/terminal-locator/${product.locationId}`
            }
            getItemKey={authData => `${authData.locationId}-${authData.productId}`}
            groupedData={groupedData}
            renderCard={renderAuthSequencingCard}
            renderGroupHeaderExtra={product => (
              <TerminalAlertsIndicator terminalId={product.locationId} />
            )}
          />
        )}
      </FilterableContent>
      {/* bool to render drawer, unmounting will clear state to initial */}
      {wizardOpen && (
        <SequencingWizard
          currentSelection={{}}
          initialStep={0}
          isLoading={false}
          setChildren={setChildren}
          setDialogOpen={() => setDialogOpen(true)}
          setOpen={setWizardOpen}
          steps={steps}
          title="Sequencing Wizard"
        >
          {children}
        </SequencingWizard>
      )}
      <MDialog
        header="Are you sure you want to close the wizard?"
        leftButtonAction={() => setDialogOpen(false)}
        leftButtonText="Back"
        onClose={() => setDialogOpen(false)}
        open={dialogOpen}
        rightButtonAction={() => {
          setWizardOpen(false);
          setDialogOpen(false);
        }}
        rightButtonText="Confirm"
      >
        <Typography variant="body2">
          By closing the wizard you will no longer be able to view the progress of each activation
          however, all requests will continue until complete. Please confirm you want to close the
          wizard.
        </Typography>
      </MDialog>
    </>
  );
};

AuthorizationSequencing.propTypes = {};

export default AuthorizationSequencing;
